import Image from 'next/image';
import {PropsWithChildren} from 'react';

import MapPinIcon from '../Icons/MapPin';
import guests from '../../../public/guests.png';
import Bed from '../Icons/Bed';
import Bath from '../Icons/Bath';

import {cn} from '@/lib/utils';
import {Village} from '@/services/types';
import {IRegionZoneDictonary} from '@/dictionaries/utils/region-zone/types';
import getMediaFilterUrl from '@/utils/get-media-filter';

type Dictionary = {
  regionZoneDict: IRegionZoneDictonary;
};

type Props = {
  village: Village;
  isMobile?: boolean;
  handleCloseModal?: () => void;
  dicts: Dictionary;
} & PropsWithChildren;

export default function VillageSideColumn({
  children,
  isMobile,
  village,
  handleCloseModal,
  dicts,
}: Props) {
  const {regionZoneDict} = dicts;
  const villageMediaCover = village.media.find(
    (media) => media.type === 'cover',
  );

  const filteredMediaCover = getMediaFilterUrl({
    mediaFilters: villageMediaCover?.mediaFilters || [],
    filterName: '480w_webp',
  });

  const villageSubtitle = `${village.city} (${
    regionZoneDict[village.zone.code]
  })`;

  return (
    <div
      className={cn(
        'hidden desktop:block relative w-[490px] ml-[-5rem] basis-auto bg-white',
        {
          'block ml-0 w-full': isMobile,
        },
      )}>
      <div className="sticky top-[5rem] z-10">
        <div
          className={cn(
            'h-auto border-b border-r border-l border-primary pb-[20px] shadow-default',
            {
              'border-0': isMobile,
            },
          )}>
          <div className="h-[12.5rem] text-white relative">
            {isMobile && (
              <button
                onClick={handleCloseModal}
                className="absolute top-[0.5rem] right-[1rem] z-10 cursor-pointer appearance-none text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 fill-current text-white"
                  viewBox="0 -960 960 960">
                  <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
                </svg>
              </button>
            )}

            <div>
              <Image
                src={
                  filteredMediaCover ??
                  `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/480w_webp/${villageMediaCover?.path}${villageMediaCover?.filename}`
                }
                alt={`Image Villa ${village.title}`}
                fill
                className="w-full h-full object-cover inline-block align-middle max-w-full"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                style={{
                  objectFit: 'cover',
                }}
              />
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-black/50" />
            </div>

            <div className="absolute top-0 left-0 w-full h-full flex flex-wrap justify-center items-center">
              <div className="text-center">
                <h3 className="block text-3xl font-title mb-2">
                  {village.title}
                </h3>
                <div className="flex items-center mb-2">
                  <MapPinIcon className="w-5 h-5 mr-[4px]" />
                  <h4 className="text-base">{villageSubtitle}</h4>
                </div>
                <div className="mt-auto flex flex-wrap justify-center items-center">
                  <Image src={guests} alt="Guests icon image" />
                  <span className="text-center ml-2">{village.sleeps}</span>
                  <Bath className="ml-8 text-white" />
                  <span className="text-center ml-2">{village.bedrooms}</span>
                  <Bed className="ml-8 text-white" />
                  <span className="text-center ml-2">{village.bathrooms}</span>
                </div>
              </div>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
